/*
 * FROSTBEULE · Card types (owner: CARDS) – <fb-card-types> custom element.
 * - Desktop: vertical ARIA tabs (click, arrow keys, Home/End) drive the card fan and the panels.
 *   The active card moves to the centre slot (--o 0), the others wrap around it (--o / --z per card).
 *   Fan cards are clickable too (pointer only – the tabs are the keyboard path).
 * - Auto-advance: the active tab's progress bar is a CSS animation (--ct-interval); its animationend
 *   selects the next type. Paused (animation-play-state) on hover, focus inside, off screen and in hidden
 *   tabs; stops for good after the first interaction. Off for motion "none" and below 990 px.
 * - Artig/Unartig switch: data-deck on the root flips every card (CSS), aria-pressed + live region;
 *   the per-type card counts swap to the chosen deck (data-count-artig / data-count-unartig).
 * - Mobile/tablet: native scroll-snap carousel; IntersectionObserver marks visible slides (.is-current
 *   starts their demo) and the pager dots scroll the track.
 * - Theme editor: survives section reloads (custom element lifecycle), block:select shows the type.
 */
(function () {
  'use strict';

  if (!('customElements' in window) || customElements.get('fb-card-types')) return;

  var DESKTOP = '(min-width: 990px)';

  function motion() {
    if (window.FROSTBEULE && typeof window.FROSTBEULE.motion === 'function') return window.FROSTBEULE.motion();
    return document.documentElement.getAttribute('data-motion') || 'full';
  }

  function toArray(list) {
    return Array.prototype.slice.call(list || []);
  }

  class FbCardTypes extends HTMLElement {
    connectedCallback() {
      if (this._bound) return;
      this._bound = true;

      this.tabs = toArray(this.querySelectorAll('[data-tab]'));
      this.panels = toArray(this.querySelectorAll('[data-panel]'));
      this.cards = toArray(this.querySelectorAll('[data-fan-card]'));
      this.slides = toArray(this.querySelectorAll('[data-slide]'));
      this.dots = toArray(this.querySelectorAll('[data-dot]'));
      this.deckBtns = toArray(this.querySelectorAll('[data-deck-btn]'));
      this.faces = toArray(this.querySelectorAll('[data-face]'));
      this.counts = toArray(this.querySelectorAll('[data-count-artig]'));
      this.track = this.querySelector('[data-track]');
      this.live = this.querySelector('[data-live]');

      this.active = 0;
      this.deck = this.getAttribute('data-deck') === 'unartig' ? 'unartig' : 'artig';
      this.stopped = false;
      this.paused = { hover: false, focus: false, view: true, hidden: document.hidden };
      this.autoplayWanted = this.getAttribute('data-autoplay') !== 'false' && this.tabs.length > 1;

      this.mql = window.matchMedia ? window.matchMedia(DESKTOP) : null;

      this.onTabClick = this.onTabClick.bind(this);
      this.onTabKey = this.onTabKey.bind(this);
      this.onCardClick = this.onCardClick.bind(this);
      this.onDeckClick = this.onDeckClick.bind(this);
      this.onDotClick = this.onDotClick.bind(this);
      this.onAnimationEnd = this.onAnimationEnd.bind(this);
      this.onPointerEnter = this.onPointerEnter.bind(this);
      this.onPointerLeave = this.onPointerLeave.bind(this);
      this.onFocusChange = this.onFocusChange.bind(this);
      this.onVisibility = this.onVisibility.bind(this);
      this.onBlockSelect = this.onBlockSelect.bind(this);
      this.updateAutoplay = this.updateAutoplay.bind(this);

      var self = this;
      this.tabs.forEach(function (tab) {
        tab.addEventListener('click', self.onTabClick);
        tab.addEventListener('keydown', self.onTabKey);
      });
      this.cards.forEach(function (card) {
        card.addEventListener('click', self.onCardClick);
      });
      this.deckBtns.forEach(function (btn) {
        btn.addEventListener('click', self.onDeckClick);
      });
      this.dots.forEach(function (dot) {
        dot.addEventListener('click', self.onDotClick);
      });

      this.addEventListener('animationend', this.onAnimationEnd);
      this.addEventListener('pointerenter', this.onPointerEnter);
      this.addEventListener('pointerleave', this.onPointerLeave);
      this.addEventListener('focusin', this.onFocusChange);
      this.addEventListener('focusout', this.onFocusChange);
      document.addEventListener('visibilitychange', this.onVisibility);
      document.addEventListener('shopify:block:select', this.onBlockSelect);
      document.addEventListener('frost:motion-change', this.updateAutoplay);
      if (this.mql) {
        if (this.mql.addEventListener) this.mql.addEventListener('change', this.updateAutoplay);
        else if (this.mql.addListener) this.mql.addListener(this.updateAutoplay);
      }

      if ('IntersectionObserver' in window) {
        this.viewIo = new IntersectionObserver(
          function (entries) {
            entries.forEach(function (entry) {
              self.paused.view = !entry.isIntersecting;
            });
            self.applyPause();
          },
          { threshold: 0.35 }
        );
        this.viewIo.observe(this);

        if (this.track && this.slides.length) {
          this.slideIo = new IntersectionObserver(
            function (entries) {
              entries.forEach(function (entry) {
                entry.target.classList.toggle('is-current', entry.intersectionRatio >= 0.6);
              });
              self.syncDots();
            },
            { root: this.track, threshold: [0, 0.6, 1] }
          );
          this.slides.forEach(function (slide) {
            self.slideIo.observe(slide);
          });
        }
      } else {
        this.paused.view = false;
      }

      this.select(0, false);
      this.applyDeck(false);
      this.updateAutoplay();
    }

    disconnectedCallback() {
      if (!this._bound) return;
      this._bound = false;
      document.removeEventListener('visibilitychange', this.onVisibility);
      document.removeEventListener('shopify:block:select', this.onBlockSelect);
      document.removeEventListener('frost:motion-change', this.updateAutoplay);
      if (this.mql) {
        if (this.mql.removeEventListener) this.mql.removeEventListener('change', this.updateAutoplay);
        else if (this.mql.removeListener) this.mql.removeListener(this.updateAutoplay);
      }
      if (this.viewIo) this.viewIo.disconnect();
      if (this.slideIo) this.slideIo.disconnect();
    }

    /* Selection ------------------------------------------------------------------------------------- */
    select(index, focusTab) {
      var count = this.tabs.length || this.cards.length;
      if (!count) return;
      var i = ((index % count) + count) % count;
      this.active = i;

      this.tabs.forEach(function (tab, k) {
        var on = k === i;
        tab.setAttribute('aria-selected', on ? 'true' : 'false');
        tab.tabIndex = on ? 0 : -1;
      });
      this.panels.forEach(function (panel, k) {
        panel.hidden = k !== i;
      });
      /* fan: the active card takes the centre slot, the others keep their order around it */
      var n = this.cards.length;
      var half = Math.floor(n / 2);
      this.cards.forEach(function (card, k) {
        var o = (((k - i) % n) + n) % n;
        if (o > half) o -= n;
        card.style.setProperty('--o', String(o));
        card.style.setProperty('--z', String(10 - Math.abs(o)));
        card.classList.toggle('is-active', o === 0);
        card.classList.toggle('is-before', o < 0);
        card.classList.toggle('is-after', o > 0);
        card.setAttribute('aria-hidden', o === 0 ? 'false' : 'true');
      });

      var source = this.tabs[i] || this.cards[i];
      var color = source && source.style.getPropertyValue('--fb-type');
      if (color) this.style.setProperty('--fb-active', color);

      if (focusTab && this.tabs[i]) this.tabs[i].focus();
    }

    onTabClick(e) {
      var i = this.tabs.indexOf(e.currentTarget);
      if (i < 0) return;
      this.stopAutoplay();
      this.select(i, false);
    }

    onTabKey(e) {
      var i = this.tabs.indexOf(e.currentTarget);
      var next = null;
      switch (e.key) {
        case 'ArrowDown':
        case 'ArrowRight':
          next = i + 1;
          break;
        case 'ArrowUp':
        case 'ArrowLeft':
          next = i - 1;
          break;
        case 'Home':
          next = 0;
          break;
        case 'End':
          next = this.tabs.length - 1;
          break;
        default:
          return;
      }
      e.preventDefault();
      this.stopAutoplay();
      this.select(next, true);
    }

    onCardClick(e) {
      var i = this.cards.indexOf(e.currentTarget);
      if (i < 0 || i === this.active) return;
      this.stopAutoplay();
      this.select(i, false);
    }

    /* Deck switch ------------------------------------------------------------------------------------ */
    onDeckClick(e) {
      var deck = e.currentTarget.getAttribute('data-deck-btn') === 'unartig' ? 'unartig' : 'artig';
      this.stopAutoplay();
      if (deck === this.deck) return;
      this.deck = deck;
      this.applyDeck(true);
    }

    applyDeck(announce) {
      var deck = this.deck;
      this.setAttribute('data-deck', deck);
      this.deckBtns.forEach(function (btn) {
        btn.setAttribute('aria-pressed', btn.getAttribute('data-deck-btn') === deck ? 'true' : 'false');
      });
      this.faces.forEach(function (face) {
        face.setAttribute('aria-hidden', face.getAttribute('data-face') === deck ? 'false' : 'true');
      });
      /* card counts per type follow the chosen deck (one deck holds half of each type) */
      this.counts.forEach(function (el) {
        var text = el.getAttribute('data-count-' + deck);
        if (text && el.textContent !== text) el.textContent = text;
      });
      if (announce && this.live) {
        this.live.textContent = this.getAttribute(deck === 'unartig' ? 'data-live-unartig' : 'data-live-artig') || '';
      }
    }

    /* Mobile carousel --------------------------------------------------------------------------------- */
    syncDots() {
      var first = -1;
      this.slides.forEach(function (slide, k) {
        if (first < 0 && slide.classList.contains('is-current')) first = k;
      });
      if (first < 0) return;
      this.dots.forEach(function (dot, k) {
        if (k === first) dot.setAttribute('aria-current', 'true');
        else dot.removeAttribute('aria-current');
      });
    }

    scrollToSlide(i) {
      var slide = this.slides[i];
      if (!slide || !this.track) return;
      var tr = this.track.getBoundingClientRect();
      var sr = slide.getBoundingClientRect();
      var left = this.track.scrollLeft + (sr.left + sr.width / 2) - (tr.left + tr.width / 2);
      try {
        this.track.scrollTo({ left: left, behavior: motion() === 'none' ? 'auto' : 'smooth' });
      } catch (err) {
        this.track.scrollLeft = left;
      }
    }

    onDotClick(e) {
      var i = this.dots.indexOf(e.currentTarget);
      if (i < 0) return;
      this.scrollToSlide(i);
    }

    /* Auto-advance ------------------------------------------------------------------------------------ */
    updateAutoplay() {
      var desktop = this.mql ? this.mql.matches : true;
      var on = this.autoplayWanted && !this.stopped && desktop && motion() !== 'none';
      this.classList.toggle('is-autoplay', on);
      this.applyPause();
    }

    stopAutoplay() {
      if (this.stopped) return;
      this.stopped = true;
      this.classList.remove('is-autoplay');
    }

    applyPause() {
      var p = this.paused;
      this.classList.toggle('is-paused', !!(p.hover || p.focus || p.view || p.hidden));
    }

    onAnimationEnd(e) {
      if (e.animationName !== 'fb-ct-progress') return;
      if (!this.classList.contains('is-autoplay') || this.classList.contains('is-paused')) return;
      this.select(this.active + 1, false);
    }

    onPointerEnter(e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      this.paused.hover = true;
      this.applyPause();
    }

    onPointerLeave(e) {
      if (e.pointerType && e.pointerType !== 'mouse') return;
      this.paused.hover = false;
      this.applyPause();
    }

    onFocusChange() {
      var self = this;
      /* focusout fires before the next element is focused – read the state one frame later */
      window.requestAnimationFrame(function () {
        self.paused.focus = self.contains(document.activeElement) && document.activeElement !== document.body;
        self.applyPause();
      });
    }

    onVisibility() {
      this.paused.hidden = document.hidden;
      this.applyPause();
    }

    /* Theme editor ------------------------------------------------------------------------------------ */
    onBlockSelect(e) {
      var detail = e.detail || {};
      if (detail.sectionId && detail.sectionId !== this.getAttribute('data-section-id')) return;
      var id = detail.blockId;
      if (!id) return;
      var i = -1;
      this.tabs.forEach(function (tab, k) {
        if (tab.getAttribute('data-block-id') === id) i = k;
      });
      if (i < 0) {
        this.slides.forEach(function (slide, k) {
          if (slide.getAttribute('data-block-id') === id) i = k;
        });
      }
      if (i < 0) return;
      this.stopAutoplay();
      this.select(i, false);
      this.scrollToSlide(i);
    }
  }

  customElements.define('fb-card-types', FbCardTypes);
})();
